import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../components/ui/accordion';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  ArrowLeft,
  Save,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  FileText,
} from 'lucide-react';
import { onboardingAPI, FilledDatasetValue, ScorePreview } from '../../lib/api';
import { useIsSupplier } from '../../hooks/usePortalMode';
import { toast } from 'sonner';
import { QuestionRenderer } from './QuestionRenderer';
import QuestionResubmissionWrapper, { getQuestionLockState } from './QuestionResubmissionWrapper';
import ScorePreviewCard from './ScorePreviewCard';
import {
  Question,
  Section,
  ConditionalLogic,
  FilledDatasetDetail,
  AnswerValue,
  statusColors,
} from './questionnaireTypes';

export default function FillQuestionnaire() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [filledDataset, setFilledDataset] = useState<FilledDatasetDetail | null>(null);
  const [answers, setAnswers] = useState<Record<number, AnswerValue>>({});
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<Record<number, boolean>>({});
  const [uploadedFiles, setUploadedFiles] = useState<Record<number, { name: string; path: string }>>({});
  // Question IDs whose answers were carried forward from a prior submission.
  // Cleared when the supplier saves a new answer for that question.
  const [carriedForwardIds, setCarriedForwardIds] = useState<Set<number>>(new Set());
  // Human-readable label for the origin of carried answers (e.g., "Issue 2, 12 Mar 2026").
  const [carriedFromLabel, setCarriedFromLabel] = useState<string | undefined>(undefined);
  const [scorePreview, setScorePreview] = useState<ScorePreview | null>(null);
  const [loadingScore, setLoadingScore] = useState(false);
  const isSupplier = useIsSupplier();

  useEffect(() => {
    if (id) {
      fetchFilledDataset();
    }
  }, [id]);

  const fetchFilledDataset = async () => {
    setLoading(true);
    try {
      const response = await onboardingAPI.getFilledDataset(Number(id));
      const data = response.data as FilledDatasetDetail;
      setFilledDataset(data);

      // Initialize answers from existing values
      const existingAnswers: Record<number, AnswerValue> = {};
      const existingFiles: Record<number, { name: string; path: string }> = {};
      if (data.values) {
        data.values.forEach((value: FilledDatasetValue) => {
          const questionId = value.question;
          if (value.file_path) {
            existingAnswers[questionId] = value.file_path;
            existingFiles[questionId] = { name: value.file_path.split('/').pop() || 'file', path: value.file_path };
          } else if (value.matrix_values && Object.keys(value.matrix_values).length > 0) {
            existingAnswers[questionId] = value.matrix_values;
          } else if (value.signature_data) {
            existingAnswers[questionId] = value.signature_data;
          } else if (value.choice_values && value.choice_values.length > 0) {
            existingAnswers[questionId] = value.choice_values;
          } else if (value.boolean_value !== null && value.boolean_value !== undefined) {
            existingAnswers[questionId] = value.boolean_value;
          } else if (value.text_value) {
            // Legacy MAB data stored every answer in text_value regardless of
            // the question type. Convert yes/no strings into booleans so the
            // yes/no RadioGroup (which expects `true` / `false`) can pre-
            // select correctly. For choice-style questions, expose the string
            // value as a single-item array so single_choice / multiple_choice
            // renderers light up their matching option.
            if (value.question_type === 'yes_no') {
              const normalised = (value.text_value || '').trim().toLowerCase();
              if (normalised === 'yes' || normalised === 'true' || normalised === '1') {
                existingAnswers[questionId] = true;
              } else if (normalised === 'no' || normalised === 'false' || normalised === '0') {
                existingAnswers[questionId] = false;
              } else {
                existingAnswers[questionId] = value.text_value;
              }
            } else if (value.question_type === 'single_choice' || value.question_type === 'multiple_choice') {
              existingAnswers[questionId] = [value.text_value];
            } else {
              existingAnswers[questionId] = value.text_value;
            }
          } else if (value.number_value !== null && value.number_value !== undefined) {
            existingAnswers[questionId] = value.number_value;
          } else if (value.date_value) {
            existingAnswers[questionId] = value.date_value;
          }
        });
      }
      setAnswers(existingAnswers);
      setUploadedFiles(existingFiles);
      setCarriedForwardIds(
        new Set((data.values ?? []).filter(v => v.carried_forward).map(v => v.question)),
      );
      const priorVersion = data.carried_forward_from_version;
      const priorDate = data.carried_forward_from_submitted_at;
      if (priorVersion || priorDate) {
        const dateStr = priorDate ? format(new Date(priorDate), 'd MMM yyyy') : null;
        setCarriedFromLabel([priorVersion, dateStr].filter(Boolean).join(', ') || undefined);
      } else {
        setCarriedFromLabel(undefined);
      }

      if (data.dataset_detail?.sections?.length) {
        setExpandedSections([`section-${data.dataset_detail.sections[0].id}`]);
      }
    } catch (error) {
      console.error('Failed to fetch filled dataset:', error);
      toast.error('Failed to load questionnaire');
      navigate('/onboarding/my-progress');
    } finally {
      setLoading(false);
    }
  };

  const fetchScorePreview = async () => {
    if (!id) return;
    setLoadingScore(true);
    try {
      const response = await onboardingAPI.previewScore(Number(id));
      setScorePreview(response.data);
    } catch (error) {
      console.error('Failed to fetch score preview:', error);
    } finally {
      setLoadingScore(false);
    }
  };

  useEffect(() => {
    if (filledDataset && !loading) {
      fetchScorePreview();
    }
  }, [filledDataset?.id, loading]);

  const handleAnswerChange = useCallback((questionId: number, value: string | number | boolean | string[] | Record<string, string | string[]>) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
    setCarriedForwardIds(prev => {
      if (!prev.has(questionId)) return prev;
      const next = new Set(prev);
      next.delete(questionId);
      return next;
    });
  }, []);

  const handleFileUpload = async (questionId: number, file: File) => {
    if (!filledDataset) return;

    setUploadingFiles(prev => ({ ...prev, [questionId]: true }));

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('question_id', questionId.toString());
      formData.append('filled_dataset_id', filledDataset.id.toString());

      const response = await onboardingAPI.uploadQuestionFile(formData);
      const { file_path, file_name } = response.data;

      setUploadedFiles(prev => ({ ...prev, [questionId]: { name: file_name, path: file_path } }));
      setAnswers(prev => ({ ...prev, [questionId]: file_path }));
      toast.success('File uploaded successfully');
    } catch (error) {
      console.error('Failed to upload file:', error);
      toast.error('Failed to upload file');
    } finally {
      setUploadingFiles(prev => ({ ...prev, [questionId]: false }));
    }
  };

  const handleFileRemove = (questionId: number) => {
    setUploadedFiles(prev => {
      const updated = { ...prev };
      delete updated[questionId];
      return updated;
    });
    setAnswers(prev => ({ ...prev, [questionId]: '' }));
  };

  // Conditional logic evaluation
  const evaluateConditionalLogic = useCallback((logic?: ConditionalLogic): boolean => {
    if (!logic || !logic.show_if || logic.show_if.length === 0) return true;

    const evaluateCondition = (condition: NonNullable<ConditionalLogic['show_if']>[0]): boolean => {
      const { question_id, operator, value } = condition;
      const answer = answers[question_id];

      const answerStr = Array.isArray(answer) ? answer.join(',') : String(answer ?? '');
      const answerIsEmpty = answer === undefined || answer === null || answer === '' ||
                           (Array.isArray(answer) && answer.length === 0);

      switch (operator) {
        case 'equals':
          if (Array.isArray(answer)) return answer.includes(String(value));
          return String(answer) === String(value) || answer === value;
        case 'not_equals':
          if (Array.isArray(answer)) return !answer.includes(String(value));
          return String(answer) !== String(value) && answer !== value;
        case 'contains':
          return answerStr.toLowerCase().includes(String(value ?? '').toLowerCase());
        case 'not_contains':
          return !answerStr.toLowerCase().includes(String(value ?? '').toLowerCase());
        case 'is_empty':
          return answerIsEmpty;
        case 'is_not_empty':
          return !answerIsEmpty;
        default:
          return true;
      }
    };

    const logicType = logic.logic || 'and';
    if (logicType === 'or') return logic.show_if.some(evaluateCondition);
    return logic.show_if.every(evaluateCondition);
  }, [answers]);

  const shouldShowSection = useCallback((section: Section): boolean => {
    return evaluateConditionalLogic(section.conditional_logic);
  }, [evaluateConditionalLogic]);

  const shouldShowQuestion = useCallback((question: Question): boolean => {
    return evaluateConditionalLogic(question.conditional_logic);
  }, [evaluateConditionalLogic]);

  const findQuestion = (questionId: number): Question | undefined => {
    if (!filledDataset?.dataset_detail?.sections) return undefined;
    for (const section of filledDataset.dataset_detail.sections) {
      const question = section.questions?.find(q => q.id === questionId);
      if (question) return question;
    }
    return undefined;
  };

  const getMissingRequiredQuestions = (): Question[] => {
    if (!filledDataset?.dataset_detail?.sections) return [];
    const missing: Question[] = [];
    for (const section of filledDataset.dataset_detail.sections) {
      if (!shouldShowSection(section)) continue;
      for (const question of section.questions || []) {
        if (!shouldShowQuestion(question)) continue;
        if (question.is_required) {
          const answer = answers[question.id];
          if (answer === undefined || answer === null || answer === '' ||
              (Array.isArray(answer) && answer.length === 0)) {
            missing.push(question);
          }
        }
      }
    }
    return missing;
  };

  const getVisibleQuestions = useCallback((): Question[] => {
    if (!filledDataset?.dataset_detail?.sections) return [];
    const visible: Question[] = [];
    for (const section of filledDataset.dataset_detail.sections) {
      if (!shouldShowSection(section)) continue;
      for (const question of section.questions || []) {
        if (shouldShowQuestion(question)) visible.push(question);
      }
    }
    return visible;
  }, [filledDataset, shouldShowSection, shouldShowQuestion]);

  const getAnsweredCount = (): number => {
    return getVisibleQuestions().filter(question => {
      const value = answers[question.id];
      return value !== undefined && value !== null && value !== '' &&
             (!Array.isArray(value) || value.length > 0);
    }).length;
  };

  const getTotalQuestions = (): number => getVisibleQuestions().length;

  const handleSave = async () => {
    if (!filledDataset) return;
    setSaving(true);
    try {
      const answersToSave = Object.entries(answers).map(([questionId, value]) => {
        const question = findQuestion(Number(questionId));
        if (!question) return null;

        const answerData: { question_id: number; text_value?: string; number_value?: number; date_value?: string; boolean_value?: boolean; choice_values?: string[]; matrix_values?: Record<string, string | string[]>; signature_data?: string } = { question_id: Number(questionId) };

        switch (question.question_type) {
          case 'text': case 'textarea': case 'email': case 'phone': case 'url':
            answerData.text_value = String(value || '');
            break;
          case 'number': case 'currency': case 'percentage': case 'rating':
            answerData.number_value = value ? Number(value) : undefined;
            break;
          case 'yes_no':
            answerData.boolean_value = value === true || value === 'yes' || value === 'true';
            break;
          case 'date':
            answerData.date_value = (value as string) || undefined;
            break;
          case 'single_choice':
            answerData.choice_values = value ? [value as string] : [];
            break;
          case 'multiple_choice':
            answerData.choice_values = Array.isArray(value) ? value : [];
            break;
          case 'file_upload':
            answerData.text_value = String(value || '');
            break;
          case 'matrix':
            answerData.matrix_values = (value as Record<string, string | string[]>) || {};
            break;
          case 'signature':
            answerData.signature_data = String(value || '');
            break;
          default:
            answerData.text_value = String(value || '');
        }
        return answerData;
      }).filter((a): a is NonNullable<typeof a> => a !== null);

      await onboardingAPI.bulkSaveAnswers(filledDataset.id, answersToSave);
      toast.success('Progress saved successfully');
      await fetchFilledDataset();
    } catch (error) {
      console.error('Failed to save answers:', error);
      toast.error('Failed to save progress');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!filledDataset) return;
    const missingRequired = getMissingRequiredQuestions();
    if (missingRequired.length > 0) {
      toast.error(`Please answer all required questions (${missingRequired.length} remaining)`);
      setShowSubmitDialog(false);
      return;
    }

    setSubmitting(true);
    try {
      await handleSave();
      await onboardingAPI.submitFilledDataset(filledDataset.id);
      toast.success('Questionnaire marked as complete');
      navigate('/onboarding/my-progress');
    } catch (error) {
      console.error('Failed to submit:', error);
      toast.error('Failed to submit questionnaire');
    } finally {
      setSubmitting(false);
      setShowSubmitDialog(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!filledDataset) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="py-12 text-center">
            <AlertTriangle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">Questionnaire not found</h3>
            <Button variant="outline" onClick={() => navigate('/onboarding/my-progress')} className="mt-4">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Onboarding
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const isReadOnly = filledDataset.status === 'submitted' ||
                     filledDataset.status === 'under_review' ||
                     filledDataset.status === 'approved';
  const totalQuestions = getTotalQuestions();
  const answeredCount = getAnsweredCount();
  const completionPercentage = totalQuestions > 0 ? Math.round((answeredCount / totalQuestions) * 100) : 0;

  // CR-5362 Phase 3: while the questionnaire is back with the supplier
  // (more_info_required), expose the customer's per-question decisions
  // so already-approved answers lock and rejected ones are flagged for
  // revision. Outside resubmission mode this lookup is a no-op.
  const resubmissionMode = filledDataset.status === 'more_info_required';
  const valuesByQuestion: Record<number, FilledDatasetValue> = {};
  (filledDataset.values ?? []).forEach((v) => {
    valuesByQuestion[v.question] = v;
  });
  const rejectedQuestionCount = resubmissionMode
    ? Object.values(valuesByQuestion).filter((v) => v.question_status === 'rejected').length
    : 0;
  const lockedQuestionCount = resubmissionMode
    ? Object.values(valuesByQuestion).filter(
        (v) => v.question_status === 'approved' || v.question_status === 'approved_with_concerns',
      ).length
    : 0;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/onboarding/my-progress')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{filledDataset.dataset_name || 'Questionnaire'}</h1>
            <p className="text-muted-foreground">
              {filledDataset.dataset_detail?.description || 'Complete the questionnaire below'}
            </p>
          </div>
        </div>
        <Badge className={statusColors[filledDataset.status] || statusColors['not_started']}>
          {filledDataset.status?.replace(/_/g, ' ')}
        </Badge>
      </div>

      {/* Progress Card */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">Progress</span>
            <span className="text-sm text-muted-foreground">
              {answeredCount} of {totalQuestions} questions answered
            </span>
          </div>
          <Progress value={completionPercentage} className="h-2" />
          <p className="text-xs text-muted-foreground mt-1">{completionPercentage}% complete</p>
        </CardContent>
      </Card>

      {scorePreview && !isSupplier && (
        <ScorePreviewCard scorePreview={scorePreview} loading={loadingScore} />
      )}

      {/* More Info Required Banner */}
      {filledDataset.status === 'more_info_required' && (
        <Card className="border-amber-500 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-600">
          <CardContent className="py-4">
            <div className="flex items-center gap-2 text-amber-800 dark:text-amber-200">
              <AlertTriangle className="h-5 w-5" />
              <span className="font-medium">More information required</span>
            </div>
            <p className="text-sm text-amber-700 dark:text-amber-300 mt-1">
              The reviewer has requested additional information. Please review the feedback below and update your answers.
            </p>
            {/* CR-5362 Phase 3: surface per-question rollup so the
                supplier knows exactly how many answers need revision
                and how many are already approved (locked). */}
            {(rejectedQuestionCount > 0 || lockedQuestionCount > 0) && (
              <div className="mt-3 flex flex-wrap gap-2">
                {rejectedQuestionCount > 0 && (
                  <Badge variant="outline" className="bg-red-100 text-red-800 border-red-300">
                    {rejectedQuestionCount} {rejectedQuestionCount === 1 ? 'question' : 'questions'} need revision
                  </Badge>
                )}
                {lockedQuestionCount > 0 && (
                  <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300">
                    {lockedQuestionCount} previously approved · locked
                  </Badge>
                )}
              </div>
            )}
            {filledDataset.more_info_feedback && (
              <div className="mt-3 p-3 bg-white dark:bg-gray-800 rounded-lg border border-amber-300 dark:border-amber-600">
                <p className="text-sm font-medium text-amber-800 dark:text-amber-200 mb-1">Reviewer's Feedback:</p>
                <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{filledDataset.more_info_feedback}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Rejected Banner */}
      {filledDataset.status === 'rejected' && (
        <Card className="border-red-500 bg-red-50 dark:bg-red-950/30 dark:border-red-600">
          <CardContent className="py-4">
            <div className="flex items-center gap-2 text-red-800 dark:text-red-200">
              <AlertTriangle className="h-5 w-5" />
              <span className="font-medium">Questionnaire Rejected</span>
            </div>
            <p className="text-sm text-red-700 dark:text-red-300 mt-1">
              This questionnaire has been rejected by the reviewer.
            </p>
            {filledDataset.rejection_reason && (
              <div className="mt-3 p-3 bg-white dark:bg-gray-800 rounded-lg border border-red-300 dark:border-red-600">
                <p className="text-sm font-medium text-red-800 dark:text-red-200 mb-1">Reason for Rejection:</p>
                <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{filledDataset.rejection_reason}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Sections */}
      <Accordion
        type="multiple"
        value={expandedSections}
        onValueChange={setExpandedSections}
        className="space-y-4"
      >
        {filledDataset.dataset_detail?.sections
          ?.filter(shouldShowSection)
          .sort((a, b) => a.sort_order - b.sort_order)
          .map((section) => (
            <AccordionItem
              key={section.id}
              value={`section-${section.id}`}
              className="border rounded-lg"
            >
              <AccordionTrigger className="px-4 hover:no-underline">
                <div className="flex items-center gap-3">
                  <FileText className="h-5 w-5 text-muted-foreground" />
                  <div className="text-left">
                    <h3 className="font-medium">{section.title}</h3>
                    {section.description && (
                      <p className="text-sm text-muted-foreground">{section.description}</p>
                    )}
                  </div>
                  <Badge variant="outline" className="ml-2">
                    {section.questions?.filter(shouldShowQuestion).length || 0} questions
                  </Badge>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4">
                <div className="space-y-4">
                  {section.questions
                    ?.filter(shouldShowQuestion)
                    .sort((a, b) => a.sort_order - b.sort_order)
                    .map((question) => {
                      const reviewedValue = valuesByQuestion[question.id];
                      const lockState = getQuestionLockState(reviewedValue, resubmissionMode);
                      return (
                        <QuestionResubmissionWrapper
                          key={question.id}
                          reviewedValue={reviewedValue}
                          resubmissionMode={resubmissionMode}
                        >
                          <QuestionRenderer
                            question={question}
                            value={answers[question.id]}
                            isReadOnly={isReadOnly || lockState.locked}
                            uploadedFile={uploadedFiles[question.id]}
                            isUploading={uploadingFiles[question.id]}
                            isCarriedForward={carriedForwardIds.has(question.id)}
                            carriedFromLabel={carriedFromLabel}
                            onAnswerChange={handleAnswerChange}
                            onFileUpload={handleFileUpload}
                            onFileRemove={handleFileRemove}
                          />
                        </QuestionResubmissionWrapper>
                      );
                    })}
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
      </Accordion>

      {/* No sections message */}
      {(!filledDataset.dataset_detail?.sections ||
        filledDataset.dataset_detail.sections.filter(shouldShowSection).length === 0) && (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium">No questions available</h3>
            <p className="text-muted-foreground">
              This questionnaire doesn't have any questions yet.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Action Buttons */}
      {!isReadOnly && (filledDataset.dataset_detail?.sections?.length ?? 0) > 0 && (
        <Card>
          <CardFooter className="flex justify-between py-4">
            <Button variant="outline" onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  Save Progress
                </>
              )}
            </Button>
            <Button onClick={() => setShowSubmitDialog(true)} disabled={saving || submitting}>
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Complete
            </Button>
          </CardFooter>
        </Card>
      )}

      {/* Read-only notice */}
      {/* CR-4762: When status === 'submitted' the old template produced
          "This questionnaire has been submitted and is submitted." — redundant
          and confusing. Suppress the trailing clause in that case and fall
          back to the plain confirmation; other statuses (approved,
          under_review, more_info_required) still surface the state. */}
      {isReadOnly && (
        <Card className="bg-muted">
          <CardContent className="py-4 text-center">
            <CheckCircle2 className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
            <p className="text-muted-foreground">
              {filledDataset.status && filledDataset.status !== 'submitted'
                ? `This questionnaire has been submitted and is ${filledDataset.status.replace(/_/g, ' ')}.`
                : 'This questionnaire has been submitted.'}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Change log — who changed what on this assessment */}
      {filledDataset.activity_log && filledDataset.activity_log.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Change log</CardTitle>
            <CardDescription>Chronological record of changes made to this assessment.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {filledDataset.activity_log.slice(0, 20).map((entry) => (
              <div key={entry.id} className="flex items-start gap-3 text-sm border-l-2 border-muted pl-3 py-1">
                <div className="flex-1 min-w-0">
                  <p className="font-medium">{entry.description}</p>
                  <p className="text-xs text-muted-foreground">
                    {entry.performed_by || 'Unknown'}
                    {entry.performed_at ? ` · ${new Date(entry.performed_at).toLocaleString()}` : ''}
                  </p>
                </div>
              </div>
            ))}
            {filledDataset.activity_log.length > 20 && (
              <p className="text-xs text-muted-foreground pt-1">
                Showing the most recent 20 of {filledDataset.activity_log.length} entries.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Submit Confirmation Dialog */}
      <AlertDialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Mark questionnaire as complete?</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to mark this questionnaire as complete. You can submit it for approval later from the My Onboarding page.
              {getMissingRequiredQuestions().length > 0 ? (
                <span className="block mt-2 text-amber-600">
                  Warning: You have {getMissingRequiredQuestions().length} required questions unanswered.
                </span>
              ) : (
                <span className="block mt-2 text-green-600">
                  All required questions have been answered.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleSubmit} disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                'Complete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
